/**
 * useWorkbenchSessionStore — Persisted session context for the Workbench.
 *
 * Holds the state that was previously lost on navigation (useState in Workbench.tsx).
 * Persisted to sessionStorage — survives tab navigation, clears on tab close.
 *
 * This replaces the manual writeDraftToSessionStorage/readDraftFromSessionStorage
 * pattern in Workbench.tsx.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { createQuotaSafeSessionStorage } from '../utils/quotaSafeStorage';

export interface WorkbenchSessionState {
    // Session context (previously useState in Workbench.tsx)
    sessionChain: string | null;
    sessionAddress: string | null;

    // Case context (so we know what we're working on when coming back)
    caseId: string | null;
    investigationId: string | null;

    // Selected node and Omnibar text, so a bare return to /workbench is usable at once
    selectedNodeId: string | null;
    omnibarText: string;

    // Timestamp for staleness detection
    lastActiveAt: number | null;

    // Actions
    setSession: (chain: string | null, address: string | null) => void;
    setCaseContext: (caseId: string | null, investigationId: string | null) => void;
    setSelectedNodeId: (id: string | null) => void;
    setOmnibarText: (text: string) => void;
    touch: () => void;
    reset: () => void;
}

const INITIAL: Pick<WorkbenchSessionState, 'sessionChain' | 'sessionAddress' | 'caseId' | 'investigationId' | 'lastActiveAt' | 'selectedNodeId' | 'omnibarText'> = {
    sessionChain: null,
    sessionAddress: null,
    caseId: null,
    investigationId: null,
    lastActiveAt: null,
    selectedNodeId: null,
    omnibarText: '',
};

export const useWorkbenchSessionStore = create<WorkbenchSessionState>()(
    persist(
        (set) => ({
            ...INITIAL,

            setSession: (chain, address) => set({
                sessionChain: chain,
                sessionAddress: address,
                lastActiveAt: Date.now(),
            }),

            setCaseContext: (caseId, investigationId) => set({
                caseId,
                investigationId,
                lastActiveAt: Date.now(),
            }),

            // Not stamped with lastActiveAt: selection and typing are not session activity.
            setSelectedNodeId: (selectedNodeId) => set((st) => (
                st.selectedNodeId === selectedNodeId ? st : { selectedNodeId }
            )),

            setOmnibarText: (omnibarText) => set((st) => (
                st.omnibarText === omnibarText ? st : { omnibarText }
            )),

            touch: () => set({ lastActiveAt: Date.now() }),

            reset: () => set({ ...INITIAL }),
        }),
        {
            name: 'workbench-session',
            storage: createJSONStorage(() => createQuotaSafeSessionStorage({
                warnKey: '__workbench_session_quota_warned__',
                warnMessage:
                    '[useWorkbenchSessionStore] sessionStorage quota exceeded — session context will not persist across reloads.',
            })),
            version: 1,
        }
    )
);
